/**
 * AIVA-9278: passive Microsoft 365 health for the status dot.
 *
 * Nothing pushes a per-user Graph failure to the add-in (expired session, revoked
 * consent, a Graph outage), and polling `/me` for it costs a call per pane per
 * tick. Instead every Graph call the add-in already makes reports its outcome
 * here, so the dot follows real traffic for free.
 *
 * Only outcomes that say something about the *service* count: a success, a 401
 * that survived the refresh-and-retry, a 5xx, or a network failure. A 400/403/
 * 404/409/429 is an answer about one request (a missing message, a denied shared
 * mailbox, throttling) and is ignored, so it never paints the whole service down.
 */

type GraphHealthListener = (reachable: boolean) => void;

const listeners = new Set<GraphHealthListener>();

export function subscribeToGraphHealth(listener: GraphHealthListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** `status` is the final HTTP status, or null when the request never got a response. */
export function reportGraphOutcome(status: number | null): void {
  let reachable: boolean;
  if (status === null) reachable = false;
  else if (status >= 200 && status < 300) reachable = true;
  else if (status === 401 || status >= 500) reachable = false;
  else return;
  listeners.forEach((listener) => listener(reachable));
}
